import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, finalize, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiCsrfBootstrap {
  private readonly http = new HttpClient(inject(HttpBackend));
  private inFlight: Observable<void> | null = null;
  private ready = false;

  load(force = false): Observable<void> {
    if (this.ready && !force) {
      return of(undefined);
    }
    if (!this.inFlight) {
      this.inFlight = this.http.get<void>(`${environment.apiUrl}/v1/auth/csrf`, { withCredentials: true }).pipe(
        tap(() => (this.ready = true)),
        finalize(() => (this.inFlight = null)),
        shareReplay(1),
      );
    }
    return this.inFlight;
  }
}

export const apiCredentialsInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const tokenExtractor = inject(HttpXsrfTokenExtractor);
  const csrfBootstrap = inject(ApiCsrfBootstrap);
  const apiUrl = new URL(environment.apiUrl, window.location.origin);
  const requestUrl = new URL(req.url, window.location.origin);
  if (
    requestUrl.origin !== apiUrl.origin ||
    !requestUrl.pathname.startsWith(`${apiUrl.pathname.replace(/\/$/, '')}/`)
  ) {
    return next(req);
  }

  if (['GET', 'HEAD', 'OPTIONS'].includes(req.method.toUpperCase()) || req.headers.has('X-XSRF-TOKEN')) {
    return next(req.clone({ withCredentials: true }));
  }

  return defer(() => {
    const token = tokenExtractor.getToken();
    const send = (): Observable<HttpEvent<unknown>> => {
      const currentToken = tokenExtractor.getToken();
      if (!currentToken) {
        throw new Error('CSRF bootstrap did not provide an XSRF token');
      }
      return next(req.clone({ withCredentials: true, headers: req.headers.set('X-XSRF-TOKEN', currentToken) }));
    };
    return csrfBootstrap.load(!token).pipe(switchMap(send));
  });
};
